import type { GoalArenaShotOutcome } from '../../../../shared/results';
import { goalArenaPresentation as P } from '../three/goalArenaPresentation';
import type { KeeperAction, ShotProfile } from './shotProfiles';

export type AimPoint = { x: number; y: number };
export type AimVector = { x: number; y: number; z: number };
/** Çizginin bittiği noktanın kale düzlemindeki yeri. `short`: kaleye ulaşmayan veya geriye sürükleme; şut sayılmaz. */
export type AimZone = 'inside' | 'post' | 'crossbar' | 'wide' | 'high' | 'short';

export const goalAimRules = {
  /** Bundan kısa sürükleme şut sayılmaz (CSS piksel). */
  minDragPx: 36,
  /** Sürüklemenin başlayabileceği, topun ekran merkezine uzaklık (CSS piksel). */
  grabRadiusPx: 88,
  /** Bu kadar dışarı şuttan sonra kale çerçevesi ipucu olarak parlar. */
  hintAfterMisses: 3,
  /** Klavye nişangâhının bir tuş basışındaki hareketi (metre). */
  keyboardStep: 0.3,
  /** Rapora yazılan deneme sayısının üst sınırı; oyunda deneme sınırsızdır. */
  maxRecordedAttempts: 999,
  /** Aşırı uzak bırakmalarda topun uçacağı dış sınır (metre). */
  maxTargetX: 9,
  maxTargetY: 6.5,
} as const;

const HALF_WIDTH = P.goal.width / 2;
const HEIGHT = P.goal.height;
const BALL_RADIUS = P.ball.radius;

/** Kamera ışınının kale çizgisi düzlemini kestiği nokta; ışın kaleye doğru değilse null. */
export function intersectGoalPlane(origin: AimVector, direction: AimVector): AimPoint | null {
  if (direction.z >= -1e-6) return null;
  const distance = (P.goal.z - origin.z) / direction.z;
  if (distance <= 0) return null;
  return { x: origin.x + direction.x * distance, y: origin.y + direction.y * distance };
}

/** Kale ağzı, topun yarım yarıçapı kadar içeriden sayılır; direk/üst direk bandı topun bir yarıçapı dışarıya uzanır. */
export function classifyAim(point: AimPoint | null): AimZone {
  if (!point || point.y < 0) return 'short';
  const x = Math.abs(point.x);
  if (x <= HALF_WIDTH - BALL_RADIUS / 2 && point.y <= HEIGHT - BALL_RADIUS / 2) return 'inside';
  if (x <= HALF_WIDTH + BALL_RADIUS && point.y <= HEIGHT + BALL_RADIUS) {
    return point.y > HEIGHT - BALL_RADIUS / 2 ? 'crossbar' : 'post';
  }
  return point.y > HEIGHT + BALL_RADIUS ? 'high' : 'wide';
}

const zoneOutcome: Record<Exclude<AimZone, 'short'>, GoalArenaShotOutcome> = {
  inside: 'goal', post: 'post', crossbar: 'crossbar', wide: 'wide', high: 'high',
};

function clamp(value: number, min: number, max: number) { return Math.min(max, Math.max(min, value)); }

function keeperFor(zone: Exclude<AimZone, 'short'>, target: AimPoint): KeeperAction {
  if (zone === 'high') return 'center';
  const height = target.y > 1.25 ? 'high' : 'low';
  // Kale içi şutta kaleci ters köşeye uçar: öğrencinin şutu her zaman gol olur.
  const side = zone === 'inside' ? (target.x >= 0 ? 'left' : 'right') : (target.x >= 0 ? 'right' : 'left');
  return `${side}-${height}`;
}

/** Öğrencinin çizdiği noktaya giden şut profili. `short` için şut üretilmez. */
export function buildAimedShotProfile(point: AimPoint, zone: Exclude<AimZone, 'short'>): ShotProfile {
  const sign = point.x >= 0 ? 1 : -1;
  let target: AimVector;
  if (zone === 'inside') {
    target = { x: clamp(point.x, -(HALF_WIDTH - BALL_RADIUS * 0.6), HALF_WIDTH - BALL_RADIUS * 0.6), y: clamp(point.y, BALL_RADIUS, HEIGHT - BALL_RADIUS * 0.6), z: P.goal.z - 0.05 };
  } else if (zone === 'post') {
    target = { x: sign * HALF_WIDTH, y: clamp(point.y, BALL_RADIUS, HEIGHT - BALL_RADIUS), z: P.goal.z + 0.15 };
  } else if (zone === 'crossbar') {
    target = { x: clamp(point.x, -HALF_WIDTH, HALF_WIDTH), y: HEIGHT, z: P.goal.z + 0.15 };
  } else {
    target = { x: clamp(point.x, -goalAimRules.maxTargetX, goalAimRules.maxTargetX), y: clamp(point.y, BALL_RADIUS, goalAimRules.maxTargetY), z: P.goal.z + 0.05 };
  }
  return {
    id: `aimed-${zone}`,
    outcome: zoneOutcome[zone],
    target,
    apexY: 1.25 + target.y * 0.95,
    curveX: 0,
    flightSeconds: 0.7 + Math.min(0.25, Math.abs(target.x) * 0.03 + target.y * 0.02),
    keeperAction: keeperFor(zone, target),
  };
}

/** Klavye nişangâhı kale ağzı çevresinde kalır; dışarı da nişan alınabilir. */
export function moveKeyboardAim(point: AimPoint, dx: number, dy: number): AimPoint {
  return {
    x: clamp(point.x + dx * goalAimRules.keyboardStep, -(HALF_WIDTH + 1.2), HALF_WIDTH + 1.2),
    y: clamp(point.y + dy * goalAimRules.keyboardStep, 0.1, HEIGHT + 1.2),
  };
}

export const initialKeyboardAim: AimPoint = { x: 0, y: HEIGHT / 2 };
